import { validateFieldValue } from "../../../utils/extensionFieldUtils";
import { ExtensionFieldCellEditor, CellEditorParams } from "./types";
import {
  CELL_EDITOR_STYLES,
  handleCellEditorKeyboard,
  handleNumericPaste,
  isAllowedKey,
  updateValidationState,
} from "./utils/cellEditorStyles";

/**
 * Percentile Cell Editor for percentile extension fields
 * Provides percentage-specific formatting and validation
 */
export class PercentileCellEditor implements ExtensionFieldCellEditor {
  private eGui: HTMLElement;
  private params!: CellEditorParams;
  private inputElement: HTMLInputElement;
  private currentValue: string;
  private initialValue: string;

  constructor() {
    this.eGui = document.createElement("div");
    Object.assign(this.eGui.style, CELL_EDITOR_STYLES.container);
    this.currentValue = "";
    this.initialValue = "";

    this.inputElement = document.createElement("input");
    this.setupInputElement();
  }

  private setupInputElement(): void {
    this.inputElement.name = "cellValue";
    this.inputElement.type = "number";
    this.inputElement.min = "0";
    this.inputElement.max = "100";
    this.inputElement.step = "0.01";
    this.inputElement.placeholder = "0-100";
    Object.assign(this.inputElement.style, CELL_EDITOR_STYLES.input);

    // Add event listeners directly (AG Grid handles optimization)
    this.inputElement.addEventListener("input", this.handleInputChange);
    this.inputElement.addEventListener("keydown", this.handleKeyDown);
    this.inputElement.addEventListener("blur", this.handleBlur);
    this.inputElement.addEventListener("paste", this.handlePaste);

    this.eGui.appendChild(this.inputElement);
  }

  private handleInputChange = (event: Event): void => {
    const target = event.target as HTMLInputElement;
    const newValue = target.value.trim(); // Simplified parsing
    this.currentValue = newValue;

    this.updateValidationState(newValue);
  };

  private updateValidationState(value: string): void {
    const validationError = validateFieldValue(value, this.params.fieldType);
    updateValidationState(this.inputElement, validationError);
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    // Handle percentile-specific validation first
    const isNumberKey = /^[0-9]$/.test(event.key);
    const isDecimalPoint = event.key === "." && !this.inputElement.value.includes(".");

    if (!isAllowedKey(event) && !isNumberKey && !isDecimalPoint) {
      event.preventDefault();
      return;
    }

    // Use shared keyboard handler for Enter/Escape behavior
    handleCellEditorKeyboard(event, {
      inputElement: this.inputElement,
      initialValue: this.initialValue,
      currentValueSetter: (value) => {
        this.currentValue = value;
      },
      api: this.params.api,
    });
  };

  private handleBlur = (): void => {
    this.params.api.stopEditing();
  };

  private handlePaste = (event: ClipboardEvent): void => {
    handleNumericPaste(event, this.inputElement, /[^0-9.]/g, (value) => {
      this.currentValue = value;
    });

    this.inputElement.dispatchEvent(new Event("input"));
  };

  init(params: CellEditorParams): void {
    this.params = params;

    // Extract initial value from the extension field value object
    const extensionFieldValue = params.value;
    const decimalValue = extensionFieldValue?.floatValue;
    if (decimalValue !== null && decimalValue !== undefined) {
      this.initialValue = decimalValue.toString();
    } else {
      this.initialValue = "";
    }
    this.currentValue = this.initialValue;
    this.inputElement.value = this.initialValue;
  }

  afterGuiAttached(): void {
    if (this.inputElement) {
      this.inputElement.focus();
      this.inputElement.select();
    }
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  getValue(): string {
    if (!this.currentValue || !this.currentValue.trim()) {
      return "";
    }
    const decimalValue = parseFloat(this.currentValue);
    if (isNaN(decimalValue)) {
      return "";
    }
    return decimalValue.toString();
  }

  destroy(): void {
    // AG Grid handles cleanup automatically, minimal cleanup needed
  }

  isPopup(): boolean {
    return false;
  }

  isCancelBeforeStart(): boolean {
    return false;
  }

  isCancelAfterEnd(): boolean {
    if (!this.params?.fieldType) return false;
    const validationError = validateFieldValue(this.currentValue, this.params.fieldType);
    return validationError !== null;
  }

  focusIn(): void {
    if (this.inputElement) {
      this.inputElement.focus();
    }
  }

  focusOut(): void {
    // No specific action needed on focus out
  }
}
